import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import TournamentSelector from '../components/TournamentSelector';
import LegSplitInput, { LegBadges } from '../components/LegSplit';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Receipt, Plus, Edit, Trash2, Calculator, AlertCircle, IndianRupee, FileText, Wallet, TrendingDown, Search, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const generateFiscalYears = () => {
  const years = [];
  for (let i = 0; i < 10; i++) {
    const startYear = 2023 + i;
    const endYear = startYear + 1;
    years.push(`${startYear}-${endYear}`);
  }
  return years;
};

// Calculate fiscal year from date
const getFiscalYearFromDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const month = date.getMonth(); // 0-11
  const year = date.getFullYear();
  
  // Fiscal year starts in April (month 3)
  if (month >= 3) {
    // April to December - current year to next year
    return `${year}-${year + 1}`;
  } else {
    // January to March - previous year to current year
    return `${year - 1}-${year}`;
  }
};

const Invoices = () => {
  const { user, hasTabAccess } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorPOs, setVendorPOs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [budgetHeadsByDept, setBudgetHeadsByDept] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPODetails, setSelectedPODetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [smartText, setSmartText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [extractMeta, setExtractMeta] = useState(null); // { matched_vendor_id, match_confidence, model }
  const [pendingDocument, setPendingDocument] = useState(null); // { filename, content_type, data_base64 }
  
  const [formData, setFormData] = useState({
    invoice_number: '',
    vendor_id: '',
    po_id: '',
    date: new Date().toISOString().split('T')[0],
    department: '',
    budget_head: '',
    taxable_value: '',
    cgst: '',
    sgst: '',
    igst: '',
    tds: '',
    fiscal_year: '',
    notes: '',
    status: 'draft',
    leg_split: null,
  });
  // Tracks whether the user has manually edited the leg_split on this dialog
  // session. We only auto-inherit from the PO until they touch it once.
  const [legSplitUserEdited, setLegSplitUserEdited] = useState(false);


  // Parse smart text input to extract Invoice details
  const parseSmartText = (text) => {
    const updates = {};
    
    // Extract Invoice number (patterns like INV-123, INV/123, BILL-123)
    const invMatch = text.match(/\b(INV[-/]?\d+|BILL[-/]?\d+)\b/i);
    if (invMatch) updates.invoice_number = invMatch[1].toUpperCase();
    
    // Extract amount (patterns like Rs.50000, ₹50,000, 50000/-)
    const amountMatch = text.match(/(?:Rs\.?|₹|INR)\s*([\d,]+(?:\.\d{2})?)|(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)\s*\/-/i);
    if (amountMatch) {
      const amount = (amountMatch[1] || amountMatch[2]).replace(/,/g, '');
      updates.taxable_value = amount;
    }
    
    // Extract date (patterns like 15/01/2024, 15-01-2024)
    const dateMatch = text.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (dateMatch) {
      const [_, day, month, year] = dateMatch;
      updates.date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Try to match vendor name from existing vendors
    const lowerText = text.toLowerCase();
    for (const vendor of vendors) {
      if (lowerText.includes(vendor.name.toLowerCase())) {
        updates.vendor_id = vendor.id;
        break;
      }
    }
    
    return updates;
  };

  const handleSmartTextSubmit = (e) => {
    if (e.key === 'Enter' && smartText.trim()) {
      const parsed = parseSmartText(smartText);
      setFormData(prev => ({
        ...prev,
        ...parsed,
        // FY is derived from the selected PO, not from the invoice date.
      }));
      setSmartText('');
      setDialogOpen(true);
      toast.success('Details extracted from text. Please review and complete the form.');
    }
  };

  // Handle invoice file selection (PDF / JPG / PNG)
  const handlePdfFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isOk =
      file.type === 'application/pdf' ||
      file.type.startsWith('image/') ||
      /\.(pdf|jpe?g|png|webp)$/i.test(file.name || '');
    if (!isOk) {
      toast.error('Please select a PDF or image (jpg/png)');
      setSelectedPdfFile(null);
      return;
    }
    setSelectedPdfFile(file);
  };

  // Handle AI-powered extraction from invoice file
  const handlePdfExtract = async () => {
    if (!selectedPdfFile) {
      toast.error('Please select an invoice file first');
      return;
    }

    setExtracting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedPdfFile);

      const response = await axios.post(`${API}/invoices/extract-from-pdf`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        const extracted = response.data.data || {};
        const doc = response.data.document || null;

        // Pre-fill form fields with extracted values (only when AI returned a value)
        setFormData((prev) => ({
          ...prev,
          invoice_number: extracted.invoice_number || prev.invoice_number,
          vendor_id: extracted.matched_vendor_id || prev.vendor_id,
          date: extracted.date || prev.date,
          taxable_value:
            extracted.taxable_value != null ? String(extracted.taxable_value) : prev.taxable_value,
          cgst: extracted.cgst != null ? String(extracted.cgst) : prev.cgst,
          sgst: extracted.sgst != null ? String(extracted.sgst) : prev.sgst,
          igst: extracted.igst != null ? String(extracted.igst) : prev.igst,
          tds: extracted.tds != null ? String(extracted.tds) : prev.tds,
          notes: extracted.notes || prev.notes,
        }));

        setExtractMeta({
          matched_vendor_id: extracted.matched_vendor_id || null,
          match_confidence: extracted.match_confidence || null,
          model: response.data.model || null,
          vendor_name_seen: extracted.vendor_name || null,
          vendor_gstin_seen: extracted.vendor_gstin || null,
        });
        setPendingDocument(doc);
        toast.success(
          extracted.matched_vendor_id
            ? 'Extracted. Vendor matched — please review all fields.'
            : 'Extracted. Please pick the vendor and review fields.'
        );
      } else {
        toast.error(response.data.message || 'Could not extract data from the file');
      }
    } catch (error) {
      console.error('Invoice extraction error:', error);
      toast.error(error.response?.data?.detail || 'Failed to extract data from the file');
    } finally {
      setExtracting(false);
      setSelectedPdfFile(null);
    }
  };

  // Filter invoices based on search term and date range
  const filteredInvoices = invoices.filter(inv => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = (
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.po_number?.toLowerCase().includes(term) ||
        inv.vendor_name?.toLowerCase().includes(term) ||
        inv.budget_head?.toLowerCase().includes(term) ||
        inv.department?.toLowerCase().includes(term) ||
        inv.notes?.toLowerCase().includes(term)
      );
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (dateFrom || dateTo) {
      const invDate = new Date(inv.date);
      if (dateFrom && invDate < new Date(dateFrom)) return false;
      if (dateTo && invDate > new Date(dateTo)) return false;
    }
    
    return true;
  });

  useEffect(() => {
    if (!selectedFiscalYear) return;
    fetchInvoices();
    fetchVendors();
    fetchDepartments(formData.fiscal_year);
  }, [selectedFiscalYear, formData.fiscal_year]);

  useEffect(() => {
    if (formData.vendor_id) {
      // When editing, ensure the linked PO is included + treat the current
      // invoice as released so the dropdown's "Open" balance reflects what's
      // actually available to re-spend on this invoice.
      fetchPOsByVendor(formData.vendor_id, editingInvoice ? {
        includePoId: editingInvoice.po_id,
        excludeInvoiceId: editingInvoice.id,
      } : {});
    } else {
      setVendorPOs([]);
      setSelectedPODetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vendor_id, editingInvoice?.id]);

  useEffect(() => {
    if (formData.po_id) {
      const po = vendorPOs.find(p => p.id === formData.po_id);
      setSelectedPODetails(po || null);
      if (po) {
        setFormData(prev => ({
          ...prev,
          department: po.department || '',
          budget_head: po.budget_head || '',
          fiscal_year: po.fiscal_year || ''
        }));
      }
    } else {
      setSelectedPODetails(null);
    }
  }, [formData.po_id]);

  // Auto-inherit per-leg split from the selected PO (parity with the PO →
  // Invoice money flow). Re-runs when the user changes the taxable value so
  // the split stays scaled. Skipped once the user manually edits the split.
  useEffect(() => {
    if (legSplitUserEdited) return;
    const po = selectedPODetails;
    if (!po) return;
    const tv = parseFloat(formData.taxable_value || 0) || 0;
    if (tv <= 0) {
      // Clear stale split when taxable is empty/zero
      if (formData.leg_split) {
        setFormData((prev) => ({ ...prev, leg_split: null }));
      }
      return;
    }
    const poSplit = po.leg_split || null;
    const poTaxable = Number(po.taxable_value || 0);
    if (!poSplit || poTaxable <= 0) {
      // Linked PO has no leg split → default to everything in common.
      const fallback = { mens: 0, womens: 0, ground2: 0, common: tv };
      setFormData((prev) => ({ ...prev, leg_split: fallback }));
      return;
    }
    const ratio = tv / poTaxable;
    const scaled = {
      mens: Number((Number(poSplit.mens || 0) * ratio).toFixed(2)),
      womens: Number((Number(poSplit.womens || 0) * ratio).toFixed(2)),
      ground2: Number((Number(poSplit.ground2 || 0) * ratio).toFixed(2)),
      common: Number((Number(poSplit.common || 0) * ratio).toFixed(2)),
    };
    const drift = Number((tv - (scaled.mens + scaled.womens + scaled.ground2 + scaled.common)).toFixed(2));
    if (Math.abs(drift) >= 0.01) {
      scaled.common = Number((scaled.common + drift).toFixed(2));
    }
    setFormData((prev) => ({ ...prev, leg_split: scaled }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPODetails, formData.taxable_value, legSplitUserEdited]);

  useEffect(() => {
    if (formData.department) {
      fetchBudgetHeadsByDepartment(formData.department, formData.fiscal_year);
    } else {
      setBudgetHeadsByDept([]);
    }
  }, [formData.department, formData.fiscal_year]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const fiscalParam = selectedFiscalYear !== 'all' ? `?fiscal_year=${selectedFiscalYear}` : '';
      const response = await axios.get(`${API}/invoices${fiscalParam}`, {
        withCredentials: true,
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, { withCredentials: true });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to load vendors');
    }
  };

  const fetchPOsByVendor = async (vendorId, opts = {}) => {
    try {
      const params = {};
      // When the user is editing an existing invoice, hand the backend the
      // invoice id + its linked PO id so:
      //   1. The linked PO is included even if it has since been marked paid.
      //   2. The "Open" balance shown in the dropdown treats this invoice's
      //      own taxable_value as released (so the user can keep the same PO
      //      and even raise the amount up to the freed-up headroom).
      if (opts.includePoId) params.include_po_id = opts.includePoId;
      if (opts.excludeInvoiceId) params.exclude_invoice_id = opts.excludeInvoiceId;
      const response = await axios.get(`${API}/purchase-orders/by-vendor/${vendorId}`, {
        params,
        withCredentials: true,
      });
      setVendorPOs(response.data);
    } catch (error) {
      console.error('Failed to load POs');
      setVendorPOs([]);
    }
  };

  const fetchDepartments = async (tournament = '') => {
    try {
      const url = tournament
        ? `${API}/metadata/departments?tournament=${encodeURIComponent(tournament)}`
        : `${API}/metadata/departments`;
      const response = await axios.get(url, { withCredentials: true });
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to load departments');
    }
  };

  const fetchBudgetHeadsByDepartment = async (department, tournament = '') => {
    try {
      const params = new URLSearchParams({ department });
      if (tournament) params.append('tournament', tournament);
      const response = await axios.get(
        `${API}/metadata/budget-heads?${params.toString()}`,
        { withCredentials: true }
      );
      // Handle both array and object response
      const budgetHeads = Array.isArray(response.data) ? response.data : (response.data.budget_heads || []);
      setBudgetHeadsByDept(budgetHeads);
    } catch (error) {
      console.error('Failed to load budget heads:', error);
      setBudgetHeadsByDept([]);
    }
  };

  const calculateNetPayable = () => {
    const taxable = parseFloat(formData.taxable_value) || 0;
    const cgst = parseFloat(formData.cgst) || 0;
    const sgst = parseFloat(formData.sgst) || 0;
    const igst = parseFloat(formData.igst) || 0;
    const tds = parseFloat(formData.tds) || 0;
    
    return taxable + cgst + sgst + igst - tds;
  };

  const resetForm = () => {
    setFormData({
      invoice_number: '',
      vendor_id: '',
      po_id: '',
      date: new Date().toISOString().split('T')[0],
      department: '',
      budget_head: '',
      taxable_value: '',
      cgst: '',
      sgst: '',
      igst: '',
      tds: '',
      fiscal_year: '',
      notes: '',
      status: 'draft',
      leg_split: null,
    });
    setLegSplitUserEdited(false);
    setEditingInvoice(null);
    setVendorPOs([]);
    setBudgetHeadsByDept([]);
    setSelectedPODetails(null);
    setSelectedPdfFile(null);
    setExtractMeta(null);
    setPendingDocument(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        taxable_value: parseFloat(formData.taxable_value) || 0,
        cgst: parseFloat(formData.cgst) || 0,
        sgst: parseFloat(formData.sgst) || 0,
        igst: parseFloat(formData.igst) || 0,
        tds: parseFloat(formData.tds) || 0,
        date: new Date(formData.date).toISOString()
      };

      // Remove vendor_id from payload as it's not needed in backend
      delete payload.vendor_id;

      if (editingInvoice) {
        await axios.put(`${API}/invoices/${editingInvoice.id}`, payload, {
          withCredentials: true,
        });
        toast.success('Invoice updated successfully');
      } else {
        const response = await axios.post(`${API}/invoices/manual`, payload, {
          withCredentials: true,
        });
        toast.success(response.data.message);
        if (response.data.po_remaining !== undefined) {
          toast.info(`PO remaining balance: ₹${response.data.po_remaining.toLocaleString('en-IN')}`);
        }
        // Auto-attach the original document the user extracted from
        const newInvoiceId = response.data.invoice_id || response.data.id;
        if (newInvoiceId && pendingDocument?.data_base64) {
          try {
            const blob = await (await fetch(
              `data:${pendingDocument.content_type};base64,${pendingDocument.data_base64}`
            )).blob();
            const fd = new FormData();
            fd.append(
              'file',
              new File([blob], pendingDocument.filename || 'invoice.pdf', {
                type: pendingDocument.content_type || 'application/pdf',
              })
            );
            await axios.post(`${API}/invoices/${newInvoiceId}/attachments`, fd, {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch (attachErr) {
            console.warn('Failed to auto-attach document', attachErr);
          }
        }
      }

      setDialogOpen(false);
      resetForm();
      setExtractMeta(null);
      setPendingDocument(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      invoice_number: invoice.invoice_number,
      vendor_id: invoice.vendor_id || '',
      po_id: invoice.po_id || '',
      date: new Date(invoice.date).toISOString().split('T')[0],
      department: invoice.department || '',
      budget_head: invoice.budget_head || '',
      taxable_value: invoice.taxable_value?.toString() || '',
      cgst: invoice.cgst?.toString() || '',
      sgst: invoice.sgst?.toString() || '',
      igst: invoice.igst?.toString() || '',
      tds: invoice.tds?.toString() || '',
      fiscal_year: invoice.fiscal_year || '',
      notes: invoice.notes || '',
      status: invoice.status || 'draft',
      leg_split: invoice.leg_split || null,
    });
    // When the invoice already has a saved leg_split, treat that as
    // user-edited so we don't overwrite it from the PO ratios on dialog open.
    setLegSplitUserEdited(Boolean(invoice.leg_split));
    setDialogOpen(true);
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice? Budget consumption will be reversed.')) {
      return;
    }

    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, { withCredentials: true });
      toast.success('Invoice deleted and budget reversed');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Group invoices by department (use filtered invoices)
  const groupedInvoices = filteredInvoices.reduce((acc, inv) => {
    const dept = inv.department || 'Uncategorized';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(inv);
    return acc;
  }, {});

  // Calculate summary metrics (use all invoices for totals)
  const summaryMetrics = {
    totalInvoices: invoices.length,
    totalTaxableValue: invoices.reduce((sum, inv) => sum + (inv.taxable_value || 0), 0),
    totalNetPayable: invoices.reduce((sum, inv) => sum + (inv.net_payable || 0), 0),
    totalPaidAmount: invoices.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0),
    totalDueAmount: invoices.reduce((sum, inv) => sum + ((inv.net_payable || 0) - (inv.paid_amount || 0)), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="invoices-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1">Manage vendor invoices</p>
        </div>
        <div className="flex gap-3 flex-wrap justify-end">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-48"
              data-testid="search-invoice-input"
            />
          </div>
          {/* Date Filters */}
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36"
              data-testid="date-from-input"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              placeholder="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36"
              data-testid="date-to-input"
            />
          </div>
          <TournamentSelector 
            value={selectedFiscalYear}
            onChange={setSelectedFiscalYear}
            showAllOption={true}
            className="w-48"
          />
          {hasTabAccess('invoices') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-invoice-button" data-write-only onClick={openNewDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingInvoice ? 'Edit Invoice' : 'Create Invoice'}
                  </DialogTitle>
                  <DialogDescription>
                    Create invoice against a Purchase Order
                  </DialogDescription>
                </DialogHeader>
                {/* Smart Fill Options - Tabs for Text and PDF */}
                <Tabs defaultValue="text" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="text" className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Quick Fill (Text)
                    </TabsTrigger>
                    <TabsTrigger value="pdf" className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      AI Extract (PDF / Image)
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="text" className="mt-2">
                    <div className="p-3 bg-muted/30 rounded-lg border border-dashed border-border">
                      <Input
                        placeholder="Paste invoice text (e.g., 'INV-123 from Vendor ABC Rs.25,000 dated 20/01/2024') and press Enter"
                        value={smartText}
                        onChange={(e) => setSmartText(e.target.value)}
                        onKeyDown={handleSmartTextSubmit}
                        className="text-sm"
                        data-testid="smart-text-input"
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="pdf" className="mt-2">
                    <div className="p-3 bg-muted/30 rounded-lg border border-dashed border-border">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                          onChange={handlePdfFileSelect}
                          className="text-sm"
                          data-testid="pdf-upload-input"
                          disabled={extracting}
                        />
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={handlePdfExtract}
                          disabled={!selectedPdfFile || extracting}
                          data-testid="ai-extract-button"
                          className="bg-gradient-to-r from-[#00204F] to-[#FF7F00] text-white hover:brightness-110"
                        >
                          {extracting ? (
                            <>
                              <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                              Reading invoice…
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Extract with AI
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Upload an invoice PDF or image. The AI will read it, match the vendor, and
                        pre-fill all fields below — review and click <strong>Save</strong> to file
                        the invoice with the document attached.
                      </p>
                      {extractMeta && (
                        <div
                          className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-1"
                          data-testid="ai-extract-summary"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">AI extracted</span>
                            {extractMeta.model && (
                              <span className="px-1.5 py-0.5 rounded bg-white border text-[10px] font-mono">
                                {extractMeta.model}
                              </span>
                            )}
                            {extractMeta.match_confidence && (
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                  extractMeta.match_confidence === 'high'
                                    ? 'bg-emerald-600 text-white'
                                    : extractMeta.match_confidence === 'medium'
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-rose-500 text-white'
                                }`}
                              >
                                Vendor match: {extractMeta.match_confidence}
                              </span>
                            )}
                          </div>
                          <div className="text-emerald-800/80">
                            Detected vendor:{' '}
                            <strong>{extractMeta.vendor_name_seen || '—'}</strong>
                            {extractMeta.vendor_gstin_seen
                              ? ` · GSTIN ${extractMeta.vendor_gstin_seen}`
                              : ''}
                            .{' '}
                            {extractMeta.matched_vendor_id
                              ? 'Matched to a vendor in your catalog.'
                              : 'No vendor in your catalog matched — please pick one below.'}
                          </div>
                          <div className="text-emerald-800/70">
                            Please review every value before saving. The original document will be
                            attached to this invoice automatically.
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="invoice_number">Invoice Number *</Label>
                    <Input
                      id="invoice_number"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                      required
                    />
                  </div>

                  {/* Vendor Selection */}
                  <div>
                    <Label htmlFor="vendor_id">Select Vendor *</Label>
                    <Select 
                      value={formData.vendor_id} 
                      onValueChange={(value) => setFormData({...formData, vendor_id: value, po_id: ''})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor to see POs" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* PO Selection */}
                  <div>
                    <Label htmlFor="po_id">Purchase Order *</Label>
                    <Select 
                      value={formData.po_id} 
                      onValueChange={(value) => setFormData({...formData, po_id: value})}
                      disabled={!formData.vendor_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={formData.vendor_id ? "Select PO" : "Select vendor first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {vendorPOs.map((po) => (
                          <SelectItem key={po.id} value={po.id}>
                            {po.po_number} - ₹{po.taxable_value?.toLocaleString('en-IN')} (Open: ₹{po.open_value?.toLocaleString('en-IN')})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.vendor_id && vendorPOs.length === 0 && (
                      <div
                        data-testid="no-pos-warning"
                        className="mt-2 flex items-start gap-3 p-3 rounded-md border border-amber-300 bg-amber-50 text-sm"
                      >
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                        <div className="flex-1">
                          <p className="font-medium text-amber-900">
                            No invoiceable POs for this vendor
                          </p>
                          <p className="text-amber-800 text-xs mt-0.5">
                            Only Draft / Approved / Final POs can receive invoices. Create a PO first.
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDialogOpen(false);
                            navigate('/purchase-orders');
                          }}
                          className="border-amber-400 text-amber-900 hover:bg-amber-100"
                        >
                          Create PO
                        </Button>
                      </div>
                    )}
                    {selectedPODetails && (
                      <div className="mt-2 p-3 bg-muted/30 rounded-lg text-sm">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-info mt-0.5" />
                          <div>
                            <p><strong>PO Details:</strong></p>
                            <p>Taxable Value: ₹{selectedPODetails.taxable_value?.toLocaleString('en-IN')}</p>
                            <p>Already Invoiced: ₹{selectedPODetails.total_invoiced?.toLocaleString('en-IN')}</p>
                            <p className="font-semibold text-primary">Available: ₹{selectedPODetails.open_value?.toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => {
                          // Date is independent of Fiscal Year — changing the
                          // invoice date should not alter the tournament/FY
                          // tagging chosen via the PO.
                          setFormData({...formData, date: e.target.value});
                        }}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="fiscal_year">Fiscal Year *</Label>
                      <Input
                        id="fiscal_year"
                        value={formData.fiscal_year}
                        readOnly
                        className="bg-muted"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="department">Department *</Label>
                      <Input
                        id="department"
                        value={formData.department}
                        readOnly
                        className="bg-muted"
                      />
                    </div>
                    <div>
                      <Label htmlFor="budget_head">Budget Head *</Label>
                      <Input
                        id="budget_head"
                        value={formData.budget_head}
                        readOnly
                        className="bg-muted"
                      />
                    </div>
                  </div>

                  {/* Tax Fields */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-sm font-semibold">Tax Calculation</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="taxable_value">Taxable Value (₹) *</Label>
                        <Input
                          id="taxable_value"
                          type="number"
                          step="0.01"
                          value={formData.taxable_value}
                          onChange={(e) => setFormData({...formData, taxable_value: e.target.value})}
                          required
                        />
                        {selectedPODetails && parseFloat(formData.taxable_value) > selectedPODetails.open_value && (
                          <p className="text-xs text-destructive mt-1">
                            Exceeds PO balance by ₹{(parseFloat(formData.taxable_value) - selectedPODetails.open_value).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="cgst">CGST (₹)</Label>
                        <Input
                          id="cgst"
                          type="number"
                          step="0.01"
                          value={formData.cgst}
                          onChange={(e) => setFormData({...formData, cgst: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="sgst">SGST (₹)</Label>
                        <Input
                          id="sgst"
                          type="number"
                          step="0.01"
                          value={formData.sgst}
                          onChange={(e) => setFormData({...formData, sgst: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="igst">IGST (₹)</Label>
                        <Input
                          id="igst"
                          type="number"
                          step="0.01"
                          value={formData.igst}
                          onChange={(e) => setFormData({...formData, igst: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="tds">TDS (₹)</Label>
                        <Input
                          id="tds"
                          type="number"
                          step="0.01"
                          value={formData.tds}
                          onChange={(e) => setFormData({...formData, tds: e.target.value})}
                        />
                      </div>
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <Label className="text-xs text-muted-foreground">Net Payable</Label>
                        <div className="text-2xl font-bold text-primary mt-1">
                          ₹{calculateNetPayable().toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({...formData, status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="final">Final</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Per-leg split — auto-inherited from the linked PO and
                      scaled by taxable_value. User can override; manual edits
                      are remembered for the rest of this dialog session. */}
                  <div className="space-y-2 border rounded-md p-3 bg-muted/30" data-testid="invoice-leg-split-block">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm font-semibold">Leg Split</Label>
                      {legSplitUserEdited && (
                        <button
                          type="button"
                          onClick={() => setLegSplitUserEdited(false)}
                          className="text-xs text-blue-600 hover:underline"
                          data-testid="invoice-leg-reset"
                        >
                          Reset to PO ratios
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Defaults to the linked PO's split scaled to this invoice's taxable value. Override if this invoice covers a different leg-mix.
                    </p>
                    <LegSplitInput
                      value={formData.leg_split}
                      taxableValue={parseFloat(formData.taxable_value) || 0}
                      onChange={(next) => {
                        setLegSplitUserEdited(true);
                        setFormData((prev) => ({ ...prev, leg_split: next }));
                      }}
                    />
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting || (selectedPODetails && parseFloat(formData.taxable_value) > selectedPODetails.open_value)}
                    >
                      {submitting ? 'Saving...' : editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-border bg-gradient-to-br from-blue-500/10 to-blue-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Invoices</p>
                <p className="text-3xl font-bold">{summaryMetrics.totalInvoices}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-emerald-500/10 to-emerald-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxable Value</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ₹{summaryMetrics.totalTaxableValue.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <IndianRupee className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-purple-500/10 to-purple-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Net Payable</p>
                <p className="text-2xl font-bold text-purple-600">
                  ₹{summaryMetrics.totalNetPayable.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Calculator className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-green-500/10 to-green-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{summaryMetrics.totalPaidAmount.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border bg-gradient-to-br from-red-500/10 to-red-600/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Due</p>
                <p className="text-2xl font-bold text-red-600">
                  ₹{summaryMetrics.totalDueAmount.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Invoices</CardTitle>
          <CardDescription>
            {searchTerm ? `Showing ${filteredInvoices.length} of ${invoices.length} invoices` : `Total: ${invoices.length} invoices`}
            {selectedFiscalYear !== 'all' && ` (FY ${selectedFiscalYear})`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">{searchTerm ? 'No matching invoices found' : 'No invoices found'}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm ? 'Try a different search term' : 'Create your first invoice to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedInvoices).map(([department, deptInvoices]) => (
                <div key={department}>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      {department}
                    </span>
                    <span className="text-muted-foreground text-sm">({deptInvoices.length} invoices)</span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/50 text-muted-foreground uppercase text-xs tracking-wider font-semibold">
                          <th className="text-left py-3 px-4">Invoice #</th>
                          <th className="text-left py-3 px-4">PO Number</th>
                          <th className="text-left py-3 px-4">Vendor</th>
                          <th className="text-left py-3 px-4">Budget Head</th>
                          <th className="text-left py-3 px-4">Date</th>
                          <th className="text-right py-3 px-4">Taxable Value</th>
                          <th className="text-right py-3 px-4">CGST</th>
                          <th className="text-right py-3 px-4">SGST</th>
                          <th className="text-right py-3 px-4">IGST</th>
                          <th className="text-right py-3 px-4">TDS</th>
                          <th className="text-right py-3 px-4">Net Payable</th>
                          <th className="text-right py-3 px-4">Paid Amount</th>
                          <th className="text-right py-3 px-4">Due Amount</th>
                          <th className="text-center py-3 px-4">Status</th>
                          {hasTabAccess('invoices') && <th className="text-center py-3 px-4">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {deptInvoices.map((invoice) => (
                          <tr
                            key={invoice.id}
                            data-testid={`invoice-row-${invoice.invoice_number}`}
                            className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                          >
                            <td className="py-3 px-4 font-mono text-sm text-primary hover:underline">{invoice.invoice_number}</td>
                            <td className="py-3 px-4 font-mono text-sm">{invoice.po_number || '-'}</td>
                            <td className="py-3 px-4">{invoice.vendor_name || '-'}</td>
                            <td className="py-3 px-4 text-sm">{invoice.budget_head}</td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {new Date(invoice.date).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4 text-right font-medium">
                              ₹{invoice.taxable_value?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm">
                              ₹{invoice.cgst?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm">
                              ₹{invoice.sgst?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm">
                              ₹{invoice.igst?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right text-sm text-destructive">
                              ₹{invoice.tds?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-primary">
                              ₹{invoice.net_payable?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-success">
                              ₹{invoice.paid_amount?.toLocaleString('en-IN') || 0}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-destructive">
                              ₹{((invoice.net_payable || 0) - (invoice.paid_amount || 0)).toLocaleString('en-IN')}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {(() => {
                                const s = (invoice.status || 'draft').toLowerCase();
                                const variantMap = {
                                  draft: 'secondary',
                                  approved: 'default',
                                  final: 'default',
                                  paid: 'success',
                                  cancelled: 'destructive',
                                };
                                const labelMap = {
                                  draft: 'Draft',
                                  approved: 'Approved',
                                  final: 'Final',
                                  paid: 'Paid',
                                  cancelled: 'Cancelled',
                                };
                                return (
                                  <Badge variant={variantMap[s] || 'secondary'}>
                                    {labelMap[s] || invoice.status}
                                  </Badge>
                                );
                              })()}
                            </td>
                            {hasTabAccess('invoices') && (
                              <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                                <div className="flex gap-2 justify-center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleEdit(invoice); }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(invoice.id); }}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Invoices;